/**
 * 프레젠테이션 탭(의사소통) → 원탭 모니터링 모달
 * LS + BroadcastChannel로 이미 로드된 보드 스냅샷을 재사용 (추가 네트워크 없음)
 */
import type { ClassMemberGroupMeta, ClassMemberRow } from './classMembersStorage';
import type { PresentationCommunicationResponse } from '../services/presentationCommunicationService';
import type { CommunicationQuestion } from './presentationCommunicationAnswers';
import {
  postBroadcastChannel,
  subscribeBroadcastChannel,
} from './noteDocTabBridgeCore';

export const COMM_MONITOR_CHANNEL = 'cp:presentation-comm-monitor:v1';
export const COMM_MONITOR_LS_KEY = 'cp.presentationCommMonitor.v1';

export type PresentationCommunicationMonitorSnapshot = {
  presentationRefId: string;
  workspaceId: string;
  promptText: string;
  answerText: string;
  question: CommunicationQuestion | null;
  groups: ClassMemberGroupMeta[];
  selectedGroupId: string | null;
  membersByGroupId: Record<string, ClassMemberRow[]>;
  responses: PresentationCommunicationResponse[];
  updatedAt: number;
  /** 있으면 원탭 모니터링 모달에도 재생/정지(+ 단일·복수) 컨트롤을 표시 */
  playState?: {
    isOpen: boolean;
    multiResponse: boolean;
    canPlay: boolean;
    disabledReason?: string;
  };
  /** 있으면 원탭 모니터링 모달에도 이전/다음 질문 탐색 버튼을 표시 */
  questionNav?: {
    canPrev: boolean;
    canNext: boolean;
    /** 1부터 시작하는 현재 질문 번호 */
    currentIndex: number;
    /** 전체 질문 수(N) */
    totalCount: number;
    /** true면 모니터링 팝업에서 새 질문 추가(+) 가능 — 저장된 질문을 보고 있을 때 */
    canAdd?: boolean;
  };
};

type SnapshotMessage = {
  type: 'snapshot';
  snapshot: PresentationCommunicationMonitorSnapshot;
};

type OpenMessage = {
  type: 'open';
  requestId: string;
  snapshot: PresentationCommunicationMonitorSnapshot;
};

type AckMessage = {
  type: 'ack';
  requestId: string;
};

/** 원탭 모니터링 모달 → 프레젠테이션 탭: 재생/정지·질문탐색 원격 실행 요청 */
export type PresentationCommunicationMonitorCommand =
  | 'play'
  | 'stop'
  | 'toggleMultiResponse'
  | 'prevQuestion'
  | 'nextQuestion'
  /** 질문 페이지의「다음(빈 슬롯)」과 같은 새 질문 슬롯으로 이동 */
  | 'addQuestion'
  /** 문제 상세 모달 저장 → 질문 페이지 저장 핸들러로 DB 반영(payload 필요) */
  | 'saveQuestion';

/** saveQuestion 명령 payload(문제 상세 모달 저장값) */
export type PresentationCommunicationMonitorQuestionPayload = {
  question: CommunicationQuestion | null;
  promptText: string;
  answerText: string;
};

type CommandMessage = {
  type: 'command';
  presentationRefId: string;
  command: PresentationCommunicationMonitorCommand;
  payload?: PresentationCommunicationMonitorQuestionPayload;
};

type BridgeMessage = SnapshotMessage | OpenMessage | AckMessage | CommandMessage;

function isBridgeMessage(data: unknown): data is BridgeMessage {
  if (!data || typeof data !== 'object') return false;
  const t = (data as { type?: unknown }).type;
  return t === 'snapshot' || t === 'open' || t === 'ack' || t === 'command';
}

function writeLs(snapshot: PresentationCommunicationMonitorSnapshot): void {
  try {
    window.localStorage.setItem(COMM_MONITOR_LS_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore quota */
  }
}

export function readPresentationCommunicationMonitorSnapshot(): PresentationCommunicationMonitorSnapshot | null {
  try {
    const raw = window.localStorage.getItem(COMM_MONITOR_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PresentationCommunicationMonitorSnapshot;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.presentationRefId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 프레젠테이션 탭: 보드 갱신 시 호출 */
export function publishPresentationCommunicationMonitorSnapshot(
  snapshot: PresentationCommunicationMonitorSnapshot
): void {
  if (typeof window === 'undefined') return;
  writeLs(snapshot);
  postBroadcastChannel(COMM_MONITOR_CHANNEL, {
    type: 'snapshot',
    snapshot,
  } satisfies SnapshotMessage);
}

/** 프레젠테이션 탭: 모니터링 버튼 → 원탭에 모달 오픈 요청 */
export function requestOpenPresentationCommunicationMonitor(
  snapshot: PresentationCommunicationMonitorSnapshot,
  requestId: string
): void {
  if (typeof window === 'undefined') return;
  writeLs(snapshot);
  postBroadcastChannel(COMM_MONITOR_CHANNEL, {
    type: 'open',
    requestId,
    snapshot,
  } satisfies OpenMessage);
}

export function ackPresentationCommunicationMonitorOpen(requestId: string): void {
  postBroadcastChannel(COMM_MONITOR_CHANNEL, {
    type: 'ack',
    requestId,
  } satisfies AckMessage);
}

/** 원탭 모니터링 모달: 재생/정지·이전/다음 질문 버튼 클릭 → 프레젠테이션 탭에 원격 실행 요청 */
export function sendPresentationCommunicationMonitorCommand(
  presentationRefId: string,
  command: PresentationCommunicationMonitorCommand,
  payload?: PresentationCommunicationMonitorQuestionPayload
): void {
  if (typeof window === 'undefined' || !presentationRefId.trim()) return;
  postBroadcastChannel(COMM_MONITOR_CHANNEL, {
    type: 'command',
    presentationRefId,
    command,
    ...(payload ? { payload } : {}),
  } satisfies CommandMessage);
}

export function subscribePresentationCommunicationMonitor(handlers: {
  onSnapshot?: (snapshot: PresentationCommunicationMonitorSnapshot) => void;
  onOpen?: (payload: {
    requestId: string;
    snapshot: PresentationCommunicationMonitorSnapshot;
  }) => void;
  onAck?: (requestId: string) => void;
  onCommand?: (payload: {
    presentationRefId: string;
    command: PresentationCommunicationMonitorCommand;
    payload?: PresentationCommunicationMonitorQuestionPayload;
  }) => void;
}): () => void {
  return subscribeBroadcastChannel<BridgeMessage>(
    COMM_MONITOR_CHANNEL,
    (msg) => {
      if (msg.type === 'snapshot') handlers.onSnapshot?.(msg.snapshot);
      else if (msg.type === 'open') {
        handlers.onOpen?.({ requestId: msg.requestId, snapshot: msg.snapshot });
      } else if (msg.type === 'ack') handlers.onAck?.(msg.requestId);
      else if (msg.type === 'command') {
        handlers.onCommand?.({
          presentationRefId: msg.presentationRefId,
          command: msg.command,
          payload: msg.payload,
        });
      }
    },
    isBridgeMessage
  );
}
